import { ethers } from 'ethers';
import { encodeFunctionData } from 'viem';

import useWrappedRPC from './useWrappedRPC';
import environments from '../utils/environments';
import { abiERC20, abiStake } from '../config/abi.contract';
import useUserStore from '../stores/user.store';
import { getSwapQuote as getSwapQuoteFromBE, buildSwapCalldata } from '../services/swap.service';

const {
  ENVIRONMENT,
  CHAIN_ID,
  FISH_TOKEN_CONTRACT_ADDRESS,
  XFISH_TOKEN_CONTRACT_ADDRESS,
  STAKE_FISH_CONTRACT_ADDRESS,
  USDC_ADDRESS,
  ROUTER_ADDRESS,
} = environments;

const DEFAULT_SWAP_SLIPPAGE = 50; // 0.50%
const INPUT_TOKEN_DECIMALS = 6; // USDC
const MOCK_FISH_EXCHANGE_RATE = 600; // 1 USDC = 600 FISH (testnet)

export default function useToken() {
  const { sendTransactionWithGas, provider } = useWrappedRPC();
  const user = useUserStore((state) => state.user);

  // ============================================================================
  // TOKEN BALANCE
  // ============================================================================

  const getFISHTokenBalance = async (address) => {
    try {
      if (!address) address = user.walletAddress;

      const tokenContract = new ethers.Contract(FISH_TOKEN_CONTRACT_ADDRESS, abiERC20, provider);
      const res = await tokenContract.balanceOf(address);
      const balanceInWei = res.toString();
      return Number(ethers.utils.formatEther(balanceInWei));
    } catch (error) {
      console.error('Get FISH balance failed:', error);
      return 0;
    }
  };

  const getXFISHTokenBalance = async (address) => {
    try {
      if (!address) address = user.walletAddress;

      const tokenContract = new ethers.Contract(XFISH_TOKEN_CONTRACT_ADDRESS, abiERC20, provider);
      const res = await tokenContract.balanceOf(address);
      const balanceInWei = res.toString();
      return Number(ethers.utils.formatEther(balanceInWei));
    } catch (error) {
      console.error('Get FISH balance failed:', error);
      return 0;
    }
  };

  const getUSDCBalance = async (address) => {
    try {
      if (!address) address = user.walletAddress;
      const tokenContract = new ethers.Contract(USDC_ADDRESS, abiERC20, provider);
      const res = await tokenContract.balanceOf(address);
      const balanceInWei = res.toString();
      return Number(ethers.utils.formatUnits(balanceInWei, 6)); // USDC has 6 decimals
    } catch (error) {
      console.error('Get USDC balance failed:', error);
      return 0;
    }
  };

  // ============================================================================
  // TOKEN APPROVAL
  // ============================================================================

  const checkAndApproveToken = async ({ tokenAddress, ownerAddress, spenderAddress, amount, tokenDecimals = 18 }) => {
    const isNativeToken = tokenAddress === ethers.constants.AddressZero;
    if (isNativeToken) return;

    const parsedAmount = ethers.utils.parseEther(`${amount}`);
    const tokenContract = new ethers.Contract(tokenAddress, abiERC20, provider);
    const res = await tokenContract.allowance(ownerAddress, spenderAddress);
    const approvedAmountInWei = res.toString();
    const approvedAmountInToken = Number(ethers.utils.parseUnits(`${approvedAmountInWei}`, tokenDecimals));
    const needApprovedMore = approvedAmountInToken < parsedAmount;

    if (needApprovedMore) {
      const tokenName = await tokenContract.name();
      const approveValueBigint =
        tokenDecimals === 18
          ? ethers.constants.MaxUint256
          : ethers.BigNumber.from('0xffffffffffffffffffffffffffffffffffffffff'); // Max for 160-bit tokens (USDC, etc.)
      const data = tokenContract.interface.encodeFunctionData('approve', [spenderAddress, approveValueBigint]);
      const txnRequest = { to: tokenAddress, data };
      const approveTxUiConfig = {
        header: 'Approve spending',
        description: `Allow Fishing Frenzy to spend ${amount} ${tokenName} on your behalf`,
        buttonText: 'Approve',
      };
      await sendTransactionWithGas(txnRequest, approveTxUiConfig);
    }
  };

  // #region STAKE
  const stakeFishToken = async (amount, durationInSeconds) => {
    try {
      if (ENVIRONMENT === 'staging') {
        durationInSeconds = mapDurationForStaging(durationInSeconds);
      }

      const parsedAmount = ethers.utils.parseEther(`${amount}`);
      const data = encodeFunctionData({
        abi: abiStake,
        functionName: 'stakeERC20',
        args: [FISH_TOKEN_CONTRACT_ADDRESS, parsedAmount, BigInt(durationInSeconds)],
      });

      await checkAndApproveToken({
        tokenAddress: FISH_TOKEN_CONTRACT_ADDRESS,
        ownerAddress: user.walletAddress,
        spenderAddress: STAKE_FISH_CONTRACT_ADDRESS,
        amount,
      });

      const transactionRequest = {
        to: STAKE_FISH_CONTRACT_ADDRESS,
        data,
      };

      const durationInMonths = durationInSeconds / (30 * 24 * 3600);
      const gainedKarma = amount * durationInMonths;
      const formattedDuration = formatDuration(durationInSeconds);
      const txUiConfig = {
        header: `Stake`,
        description: `Stake ${amount} FISH for ${formattedDuration} to gain ${gainedKarma.toLocaleString()} Karma`,
        buttonText: `Confirm`,
      };

      const receipt = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!receipt) throw new Error('Transaction failed on blockchain');

      const updatedBalance = await getFISHTokenBalance();
      console.log('Updated FISH balance:', updatedBalance.toLocaleString());

      const formattedKarma = (Math.round(gainedKarma * 100) / 100).toLocaleString();

      return {
        success: true,
        message: `Stake successful:\n+${formattedKarma} Karma`,
        data: { gainedKarma, fishTokenBalance: updatedBalance },
      };
    } catch (error) {
      console.error('Stake FISH failed:', error);
      return { success: false, message: error.message };
    }
  };

  // #region SWAP

  const swapUsdcToFish = async (rawAmount) => {
    try {
      console.log(`🔄 Starting swap: ${rawAmount} USDC → FISH`);
      const isTestnet = Number(CHAIN_ID) !== 2020;

      // Approve USDC spending for router
      await checkAndApproveToken({
        tokenAddress: USDC_ADDRESS,
        ownerAddress: user.walletAddress,
        spenderAddress: ROUTER_ADDRESS,
        amount: rawAmount, // Pass as number, function will parse it
        tokenDecimals: INPUT_TOKEN_DECIMALS,
      });

      // Get swap calldata from Ronin API
      const { calldata: calldataResponse, quoteData } = await getSwapCalldata(rawAmount);
      const expectedFish = parseFloat(quoteData.quoteData.quoteDecimals);

      // Execute swap
      const txReceipt = await sendTransactionWithGas(
        {
          to: calldataResponse.to,
          data: calldataResponse.data,
          value: calldataResponse.value || '0',
        },
        {
          header: 'Swap',
          description: isTestnet
            ? `Testnet Mock: Transfer ${rawAmount.toLocaleString()} USDC to self`
            : `Swap ${rawAmount.toLocaleString()} USDC to FISH`,
          buttonText: 'Confirm Swap',
        }
      );

      // Get receipt and extract swapped amount
      const receipt = await provider.getTransactionReceipt(txReceipt.hash);
      console.log('📦 Transaction receipt:', receipt);
      // Handle testnet vs mainnet differently
      if (isTestnet) {
        console.log('🧪 Testnet swap completed (mock transaction)');
        console.log(`Expected output: ~${expectedFish.toFixed(2)} FISH`);

        // For testnet, use the expected amount from quote
        const updatedBalance = await getFISHTokenBalance();

        return {
          success: true,
          message: `Testnet Mock Swap:\nTransferred ${rawAmount.toLocaleString()} USDC to self\n\nExpected output: ~${(
            Math.round(expectedFish * 100) / 100
          ).toLocaleString()} FISH\n\n⚠️ Note: Real swaps only available on mainnet`,
          data: {
            transactionHash: txReceipt.hash,
            swappedFishAmount: expectedFish, // Use expected amount for testnet
            fishTokenBalance: updatedBalance,
            isTestnet: true,
            mockSwap: true,
          },
        };
      }

      // Mainnet - extract actual swapped amount from receipt
      const swappedFishAmountInHex = getSwapTokenOutAmountFromReceipt(
        receipt,
        user.walletAddress,
        FISH_TOKEN_CONTRACT_ADDRESS
      );
      const swappedFishAmount = parseFloat(ethers.utils.formatEther(swappedFishAmountInHex));
      console.log(`✨ Swapped ${rawAmount} USDC → ${swappedFishAmount.toFixed(4)} FISH`);

      const updatedBalance = await getFISHTokenBalance();

      return {
        success: true,
        message: `Spent ${rawAmount.toLocaleString()} USDC and got\n${(
          Math.round(swappedFishAmount * 100) / 100
        ).toLocaleString()} FISH`,
        data: {
          transactionHash: txReceipt.hash,
          swappedFishAmount,
          fishTokenBalance: updatedBalance,
        },
      };
    } catch (error) {
      console.error('❌ Swap USDC to FISH error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Swap failed',
      };
    }
  };

  /**
   * Get swap calldata from Ronin's build API
   * Two-step process:
   * 1. Get route/quote from routes API
   * 2. Build calldata using route info
   */
  const getSwapCalldata = async (rawAmount) => {
    try {
      const isTestnet = Number(CHAIN_ID) === 2021;
      const quoteData = await getSwapQuote(rawAmount);
      const { route } = quoteData;

      // For testnet, generate mock calldata (USDC transfer to self)
      if (isTestnet) {
        console.warn('⚠️ Using testnet mock calldata - USDC transfer to self (no actual swap)');

        const amount_in = ethers.utils.parseUnits(`${rawAmount}`, INPUT_TOKEN_DECIMALS);

        // Create a transfer-to-self transaction using USDC contract
        const mockData = encodeFunctionData({
          abi: abiERC20,
          functionName: 'transfer',
          args: [user.walletAddress, amount_in],
        });

        return {
          to: USDC_ADDRESS, // Transfer USDC to self
          data: mockData,
          value: '0',
        };
      }

      // For mainnet, use the build API
      const buildResponse = await buildSwapCalldata({
        chain_id: Number(CHAIN_ID),
        provider: route.provider,
        slippage_tolerance_bps: DEFAULT_SWAP_SLIPPAGE,
        calldata_builder: route.calldata_builder,
      });

      const { data } = buildResponse.data;

      return { calldata: data, quoteData };
    } catch (error) {
      console.error('🚨 Failed to get swap calldata:', {
        message: error.message,
        response: error.response?.data,
      });
      throw new Error(error.response?.data?.message || error.message || 'Failed to build swap calldata');
    }
  };

  // #region SWAP HELPERS
  /**
   * Get swap quote for preview (supports testnet with mock data)
   */
  const getSwapQuote = async (rawAmount) => {
    const amount_in = ethers.utils.parseUnits(`${rawAmount}`, INPUT_TOKEN_DECIMALS);
    const isTestnet = Number(CHAIN_ID) !== 2020;

    if (isTestnet) {
      console.warn('⚠️ Using testnet mock quote (no API support)');
      const mockOutputAmount = ethers.utils.parseEther((parseFloat(rawAmount) * MOCK_FISH_EXCHANGE_RATE).toString());
      const minimumAmountOut = mockOutputAmount.mul(10000 - DEFAULT_SWAP_SLIPPAGE).div(10000);

      // Generate mock request_id for testnet
      const mockRequestId = `0x${ethers.utils.hexlify(ethers.utils.randomBytes(32)).slice(2)}`;

      // Return full route structure matching mainnet API format
      return {
        route: {
          amount_out: mockOutputAmount.toString(),
          price_impact: '0.5',
          provider: 'testnet-mock',
          calldata_builder: {
            strategy: 'relay_sdk',
            request_id: mockRequestId,
            data: JSON.stringify({
              steps: [
                {
                  id: 'swap',
                  action: 'Confirm swap (testnet mock)',
                  token_in: USDC_ADDRESS,
                  token_out: FISH_TOKEN_CONTRACT_ADDRESS,
                  amount_in: amount_in.toString(),
                  amount_out: mockOutputAmount.toString(),
                },
              ],
            }),
          },
        },
        minimumAmountOut,
        quoteData: {
          quoteDecimals: ethers.utils.formatEther(mockOutputAmount),
          amount_out: mockOutputAmount.toString(),
          price_impact: '0.5',
          provider: 'testnet-mock',
        },
        amountIn: amount_in,
      };
    }

    // Mainnet - use actual API
    const response = await getSwapQuoteFromBE({
      chain_id: CHAIN_ID,
      token_in: USDC_ADDRESS,
      token_out: FISH_TOKEN_CONTRACT_ADDRESS,
      amount_in: amount_in.toString(),
      slippage_tolerance_bps: DEFAULT_SWAP_SLIPPAGE,
    });

    const { routes } = response.data;
    if (!routes || routes.length === 0) throw new Error('No routes found');

    const bestRoute = routes.reduce((best, current) => {
      const betterRate = ethers.BigNumber.from(current.amount_out).gt(ethers.BigNumber.from(best.amount_out));
      const hasCalldataRequestId = Boolean(current.calldata_builder.request_id);
      const bestHasNoCalldataRequestId = !best.calldata_builder.request_id;

      return (betterRate || bestHasNoCalldataRequestId) && hasCalldataRequestId ? current : best;
    }, routes[0]);

    const amountOut = ethers.BigNumber.from(bestRoute.amount_out);
    const minimumAmountOut = amountOut.mul(10000 - DEFAULT_SWAP_SLIPPAGE).div(10000);

    return {
      route: bestRoute,
      minimumAmountOut,
      quoteData: {
        quoteDecimals: ethers.utils.formatEther(bestRoute.amount_out),
        amount_out: bestRoute.amount_out,
        price_impact: bestRoute.price_impact,
        provider: bestRoute.provider,
      },
      amountIn: amount_in,
    };
  };

  const getSwapPreview = async ({ inputToken, amount }) => {
    try {
      let inputAmount, outputAmount, minimumOutput;
      if (inputToken === 'USDC') {
        const res = await getPreviewSwapOutput({ amount });
        if (!res.success) throw new Error(res.error || 'Preview failed');
        inputAmount = amount;
        ({ outputAmount, minimumOutput } = res);
      } else {
        const maxInput = 100; // upper bound guess
        const res = await getPreviewSwapInput(amount, maxInput);
        if (!res) throw new Error('Preview failed');
        outputAmount = amount;
        minimumOutput = amount;
        ({ inputAmount } = res);
      }

      return {
        success: true,
        data: {
          inputToken: 'USDC',
          outputToken: 'FISH',
          inputAmount,
          outputAmount,
          minimumOutput,
        },
      };
    } catch (error) {
      console.error('Get preview swap output failed:', error);
      return {
        success: false,
        error: error.message || 'Failed to get quote',
      };
    }
  };

  /**
   * Get preview of swap output for user input
   * @param {Object} params
   * @param {string} params.inputToken - 'USDC' or 'FISH'
   * @param {string|number} params.amount - Amount to swap
   * @returns {Promise<Object>} Preview data with output amount, price impact, etc.
   */
  const getPreviewSwapOutput = async ({ amount }) => {
    if (!amount || amount <= 0) {
      return null;
    }

    const roundedAmount = parseFloat(amount).toFixed(INPUT_TOKEN_DECIMALS);

    // Get quote using shared function
    const { minimumAmountOut, quoteData, amountIn } = await getSwapQuote(roundedAmount);
    // Format amounts
    const inputAmount = ethers.utils.formatUnits(amountIn, INPUT_TOKEN_DECIMALS);
    const outputAmount = quoteData.quoteDecimals;
    const minimumOutput = ethers.utils.formatEther(minimumAmountOut);

    // Calculate price (how much output per 1 input)
    const price = parseFloat(outputAmount) / parseFloat(inputAmount);

    return {
      success: true,
      inputToken: 'USDC',
      outputToken: 'FISH',
      inputAmount: parseFloat(inputAmount),
      outputAmount: parseFloat(outputAmount),
      minimumOutput: parseFloat(minimumOutput),
      price, // e.g., 600 FISH per 1 USDC
      priceImpact: parseFloat(quoteData.price_impact),
      slippage: DEFAULT_SWAP_SLIPPAGE / 100, // 0.5%
      route: 'USDC → FISH',
    };
  };

  /**
   * Optimized reverse swap calculation using Newton's method
   * Finds required input amount for desired output with minimal API calls
   */
  const getPreviewSwapInput = async (
    targetOut, // desired FISH amount
    maxInput = 100, // upper bound guess
    tolerance = 0.0001 // tolerance % in output amount (FISH) // 0.01% here
  ) => {
    const MAX_ITERATIONS = 8; // Limit iterations to prevent infinite loops
    let iteration = 0;
    let fishPrice = MOCK_FISH_EXCHANGE_RATE; // Initial dummy price

    // Initial guess: use a reasonable estimate based on typical swap rates
    let currentGuess = Math.min(targetOut / fishPrice, maxInput / 2);

    let lastOutput = 0;
    let lastInput = 0;
    const toleranceAmount = tolerance * targetOut;

    console.log(`🎯 Target: ${targetOut} FISH. ${toleranceAmount.toFixed(2)} tolerance (${tolerance * 100}%)`);

    while (iteration < MAX_ITERATIONS) {
      iteration++;

      // Get output for current guess
      const res = await getPreviewSwapOutput({ amount: currentGuess });
      if (!res.success) throw new Error('Preview failed');

      const { outputAmount, inputAmount, price } = res;
      fishPrice = price; // Update price estimate
      const error = targetOut - outputAmount; // Positive = need more, Negative = got extra

      console.log(
        `📊 Iteration ${iteration}: ${currentGuess.toFixed(INPUT_TOKEN_DECIMALS)} USDC → ${outputAmount.toFixed(
          2
        )} FISH (error: ${error.toFixed(2)})`
      );

      // CRITICAL: Only accept if we meet or exceed target AND within tolerance
      // error <= 0 means outputAmount >= targetOut (we got enough)
      // Math.abs(error) <= toleranceAmount means we're not too far over
      if (error <= 0 && Math.abs(error) <= toleranceAmount) {
        console.log(
          `✅ Converged in ${iteration} iterations (+${Math.abs(error).toFixed(2)} FISH extra, within tolerance)`
        );
        return {
          inputAmount: currentGuess,
          outputAmount,
          minimumOutput: res.minimumOutput,
        };
      }

      // Newton's method: use derivative (rate of change) to adjust guess
      if (iteration > 1 && lastInput !== currentGuess) {
        // Calculate approximate derivative: dOutput/dInput
        const derivative = (outputAmount - lastOutput) / (currentGuess - lastInput);

        if (derivative > 0) {
          // Newton step: newGuess = currentGuess + error/derivative
          const step = error / derivative;

          // Adaptive damping: more aggressive when undershooting, conservative when overshooting
          const dampingFactor = error > 0 ? 0.9 : 0.5;
          const nextGuess = currentGuess + step * dampingFactor;

          // Store current values for next iteration
          lastOutput = outputAmount;
          lastInput = currentGuess;

          // Clamp to reasonable bounds
          currentGuess = Math.max(0.001, Math.min(maxInput, nextGuess));
        } else {
          // Derivative is 0 or negative (shouldn't happen), fall back to binary search step
          currentGuess = error > 0 ? currentGuess * 1.1 : currentGuess * 0.95;
        }
      } else {
        // First iteration: adjust based on error magnitude
        lastOutput = outputAmount;
        lastInput = currentGuess;

        if (error > 0) {
          // Need more input (undershooting)
          const scaleFactor = 1 + Math.min(error / outputAmount, 0.5); // max 50% jump
          currentGuess *= scaleFactor;
        } else {
          // Got too much output (overshooting) - reduce more carefully
          const scaleFactor = 1 - Math.min(Math.abs(error) / outputAmount, 0.2); // max 20% reduction
          currentGuess *= scaleFactor;
        }

        currentGuess = Math.max(0.001, Math.min(maxInput, currentGuess));
      }
    }

    // If we exhausted iterations, ensure we meet the minimum requirement
    console.log(`⚠️ Max iterations reached. Ensuring minimum output...`);

    let finalGuess = currentGuess;
    const checkRes = await getPreviewSwapOutput({ amount: finalGuess });

    if (checkRes.outputAmount < targetOut) {
      // We're short, increase input with a safety margin
      const shortfall = targetOut - checkRes.outputAmount;
      const safetyFactor = 1.02; // 2% safety margin
      finalGuess = finalGuess * (1 + shortfall / checkRes.outputAmount) * safetyFactor;

      const finalRes = await getPreviewSwapOutput({ amount: finalGuess });
      console.log(
        `📈 Adjusted to ${finalGuess.toFixed(INPUT_TOKEN_DECIMALS)} USDC → ${finalRes.outputAmount.toFixed(2)} FISH`
      );

      return {
        inputAmount: finalGuess,
        outputAmount: finalRes.outputAmount,
        minimumOutput: finalRes.minimumOutput,
      };
    }

    return {
      inputAmount: finalGuess,
      outputAmount: checkRes.outputAmount,
      minimumOutput: checkRes.minimumOutput,
    };
  };

  return {
    getFISHTokenBalance,
    getXFISHTokenBalance,
    getUSDCBalance,
    checkAndApproveToken,
    stakeFishToken,
    swapUsdcToFish,
    getSwapPreview,
  };
}

// #region HELPER FUNCS

function formatDuration(seconds) {
  const units = [
    { label: 'month', secs: 30 * 24 * 3600 },
    { label: 'day', secs: 24 * 3600 },
    { label: 'hour', secs: 3600 },
    { label: 'minute', secs: 60 },
    { label: 'second', secs: 1 },
  ];

  for (let { label, secs } of units) {
    if (seconds >= secs) {
      const value = Math.floor(seconds / secs);
      return `${value} ${label}${value > 1 ? 's' : ''}`;
    }
  }
}

function mapDurationForStaging(durationInSeconds) {
  const MONTH_IN_SECONDS = 30 * 24 * 60 * 60;
  const mapping = {
    [1 * MONTH_IN_SECONDS]: 5 * 60,
    [3 * MONTH_IN_SECONDS]: 10 * 60,
    [12 * MONTH_IN_SECONDS]: 15 * 60,
  };
  return mapping[durationInSeconds] ?? durationInSeconds;
}

// ============================================================================
// QUOTE & TRADE HELPERS
// ============================================================================

/**
 * Extract swap output from transaction receipt
 */
const getSwapTokenOutAmountFromReceipt = (receipt, userAddress, outputTokenAddress) => {
  const TRANSFER_TOPIC = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef';
  const normalizedUser = `0x${userAddress.toLowerCase().replace('0x', '').padStart(64, '0')}`;
  const normalizedToken = outputTokenAddress.toLowerCase();

  const transferLog = receipt.logs.find((log) => {
    return (
      log.topics[0] === TRANSFER_TOPIC &&
      log.address.toLowerCase() === normalizedToken &&
      log.topics[2]?.toLowerCase() === normalizedUser
    );
  });

  if (!transferLog) {
    throw new Error('Transfer event not found in receipt');
  }

  return transferLog.data;
};

/**
 * Transform new Katana API route format to the old format expected by transformQuoteToTrade
 * @param {Object} route - Route from new API format
 * @param {Object} params - Original query params
 * @returns {Object} Transformed data in old format
 */
function transformKatanaRouteToTrade(route, params) {
  // The new API returns routes[0] which is an array of swap steps
  const swapSteps = route.routes[0];

  // Transform each step to the old pool format
  const transformedRoute = swapSteps.map((step) => {
    // Detect if it's a V3 pool (you might need to adjust this logic)
    const isV3 = step.pool_type === 'v3-pool';

    if (isV3) {
      return {
        type: 'v3-pool',
        tokenIn: {
          address: step.token_in.address,
          decimals: step.token_in.decimal.toString(),
          symbol: step.token_in.symbol,
        },
        tokenOut: {
          address: step.token_out.address,
          decimals: step.token_out.decimal.toString(),
          symbol: step.token_out.symbol,
        },
        fee: step.fee || 3000, // Default to 0.3% if not provided
        sqrtRatioX96: step.sqrtRatioX96 || '0',
        liquidity: step.liquidity || '0',
        tickCurrent: step.tickCurrent || '0',
        amountOut: step.amount_out,
      };
    } else {
      // V2 pool format
      return {
        type: 'v2-pool',
        reserve0: {
          token: {
            address: step.token_in.address,
            decimals: step.token_in.decimal.toString(),
            symbol: step.token_in.symbol,
          },
          quotient: step.reserve0 || '1000000000000000000',
        },
        reserve1: {
          token: {
            address: step.token_out.address,
            decimals: step.token_out.decimal.toString(),
            symbol: step.token_out.symbol,
          },
          quotient: step.reserve1 || '1000000000000000000',
        },
        tokenIn: {
          address: step.token_in.address,
          decimals: step.token_in.decimal.toString(),
          symbol: step.token_in.symbol,
        },
        tokenOut: {
          address: step.token_out.address,
          decimals: step.token_out.decimal.toString(),
          symbol: step.token_out.symbol,
        },
        amountOut: step.amount_out,
      };
    }
  });

  return {
    route: [transformedRoute],
    quoteDecimals: ethers.utils.formatEther(route.amount_out),
  };
}
